import React, { useState } from "react";
import { Button, Icon } from "common/components";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterListIcon from "@mui/icons-material/FilterList";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { DataSchema, DataFilter } from "../../data/models";
import { MainRoutes } from "common/config/route.config";
import "./DataFilterBuilder.scss";

interface DataFilterBuilderProps {
  schema: DataSchema;
  selectedColumns: string[];
  onContinue: (filters: DataFilter[]) => void;
  onBack: () => void;
  backRoute?: string;
}

const DataFilterBuilder: React.FC<DataFilterBuilderProps> = ({
  schema,
  selectedColumns,
  onContinue,
  onBack,
  backRoute
}) => {
  const [filters, setFilters] = useState<DataFilter[]>([]);
  const [estimatedRows, setEstimatedRows] = useState(schema.recordCount);

  const addFilter = () => {
    setFilters([
      ...filters,
      {
        column: selectedColumns[0],
        operator: "equals",
        value: ""
      }
    ]);
  };

  const updateFilter = (index: number, field: keyof DataFilter, value: any) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    setFilters(newFilters);

    // Simulate row count estimation
    const reductionFactor = Math.pow(0.7, newFilters.length);
    setEstimatedRows(Math.floor(schema.recordCount * reductionFactor));
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleContinue = () => {
    onContinue(filters);
  };

  return (
    <>
      <HeaderNew showSearch={false} />

      <div className="data-filter-builder-hub">
        <div className="hub-content">
          <HubHeader
            title="Data Integration Hub"
            description="Centralized platform for seamless data integration and workflow management."
            showBackButton={true}
            backRoute={backRoute || MainRoutes.AVAILABLE_DETAILS}
          />

          <div className="section-header">
            <div className="section-info">
              <h2>Filter Data</h2>
              <p>
                Apply filters to{" "}
                <span className="font-medium">{schema.tableName}</span> to
                narrow down the data
              </p>
            </div>
          </div>

          <div className="filter-builder-content">
            <div className="selected-columns-section">
              <div className="section-card">
                <div className="card-header">
                  <h3>Selected Columns</h3>
                  <span className="columns-count">
                    {selectedColumns.length} columns
                  </span>
                </div>
                <div className="columns-list">
                  {selectedColumns.map((column) => (
                    <span key={column} className="column-badge">
                      {column}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="filters-section">
              <div className="section-card">
                <div className="card-header">
                  <div className="header-info">
                    <h3>Filter Conditions</h3>
                    <p>Add conditions to filter the data before processing</p>
                  </div>
                  <Button onClick={addFilter} variant="primary">
                    <AddIcon /> Add Filter
                  </Button>
                </div>

                {filters.length === 0 ? (
                  <div className="empty-state">
                    <FilterListIcon className="empty-icon" />
                    <p>No filters applied. All data will be processed.</p>
                  </div>
                ) : (
                  <div className="filters-list">
                    {filters.map((filter, index) => (
                      <div key={index} className="filter-item">
                        <div className="filter-fields">
                          <div className="field-group">
                            <label>Column</label>
                            <select
                              value={filter.column}
                              onChange={(e) =>
                                updateFilter(index, "column", e.target.value)
                              }
                              className="filter-select"
                            >
                              {selectedColumns.map((col) => (
                                <option key={col} value={col}>
                                  {col}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="field-group">
                            <label>Operator</label>
                            <select
                              value={filter.operator}
                              onChange={(e) =>
                                updateFilter(
                                  index,
                                  "operator",
                                  e.target.value as any
                                )
                              }
                              className="filter-select"
                            >
                              <option value="equals">Equals</option>
                              <option value="contains">Contains</option>
                              <option value="greater_than">Greater Than</option>
                              <option value="less_than">Less Than</option>
                              <option value="between">Between</option>
                            </select>
                          </div>

                          <div className="field-group">
                            <label>Value</label>
                            <input
                              type="text"
                              value={filter.value}
                              onChange={(e) =>
                                updateFilter(index, "value", e.target.value)
                              }
                              placeholder="Enter value"
                              className="filter-input"
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => removeFilter(index)}
                          className="remove-filter-btn"
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="estimation-panel">
                  <div className="estimation-content">
                    <div className="estimation-info">
                      <div className="estimation-label">Estimated Result</div>
                      <div className="estimation-subtitle">
                        Based on applied filters
                      </div>
                    </div>
                    <div className="estimation-value">
                      {estimatedRows.toLocaleString()} rows
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <div></div>
              <Button onClick={handleContinue} variant="primary">
                Continue to Mapping
                <ArrowForwardIcon />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataFilterBuilder;
